import React, { useEffect, useState } from "react";
import { ArrowRightLeft } from "lucide-react";
import { AppButton, AppModal, Callout, Checkbox, FieldGroup, FormField, Textarea } from "../../ui";
import apiClient from "../../../utils/apiClient";
import { labelOf, nounOf } from "./scheduleFormat";

const FORM_ID = "syllabus-carry-over-form";

/**
 * CarryOverDialog — a class did not get through everything: its unticked
 * topics move to the front of the next class still to be taught (or a new
 * last class, if none is left). Dates do not change.
 *
 * When the class they land in then runs over its length, the result says so
 * and `onOverrun` lets the caller offer a re-pack.
 *
 * Props:
 *   show, onHide, batchId
 *   session — the class (row) the topics come from
 *   onDone(message), onOverrun(overrun) — overrun is { minutes, classMinutes }
 */
const CarryOverDialog = ({ show, onHide, batchId, session, onDone, onOverrun }) => {
  const pending = (session?.topics || [])
    .map((t, index) => ({ ...t, index }))
    .filter((t) => t.status !== "completed");
  const [chosen, setChosen] = useState(() => new Set());
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState("");

  const pendingKey = pending.map((t) => t.index).join(",");
  useEffect(() => {
    if (!show) return;
    setChosen(new Set(pendingKey ? pendingKey.split(",").map(Number) : []));
    setReason("");
    setFailure("");
    setSaving(false);
  }, [show, pendingKey]);

  if (!session) return null;
  const noun = nounOf(session);

  const submit = async (e) => {
    e.preventDefault();
    if (!chosen.size) {
      setFailure("Choose at least one topic to carry over.");
      return;
    }
    setSaving(true);
    setFailure("");
    try {
      const { data } = await apiClient.post(`/syllabus/batch/${batchId}/syllabus/${session.dayNumber}/carry-over`, {
        topicIndexes: [...chosen],
        reason: reason.trim(),
      });
      onDone?.(data.message);
      onHide();
      if (data.overrun) onOverrun?.(data.overrun);
    } catch (err) {
      setFailure(err?.response?.data?.message || "The topics were not carried over. Try again.");
      setSaving(false);
    }
  };

  return (
    <AppModal
      show={show}
      onHide={saving ? undefined : onHide}
      size="md"
      title={`Carry over from ${labelOf(session)}`}
      icon={<ArrowRightLeft size={18} aria-hidden="true" />}
      footer={(
        <div className="tw-flex tw-w-full tw-justify-end tw-gap-2">
          <AppButton variant="outline" onClick={onHide} disabled={saving}>Not now</AppButton>
          <AppButton type="submit" form={FORM_ID} disabled={saving || !pending.length}>
            {saving ? "Moving…" : "Carry them over"}
          </AppButton>
        </div>
      )}
    >
      <form id={FORM_ID} onSubmit={submit} noValidate className="tw-flex tw-flex-col tw-gap-4">
        <p className="tw-m-0 tw-text-sm tw-text-content-muted">
          These topics weren’t ticked as covered. Carried over, they go to the front of the next {noun} still to be taught —
          no dates move.
        </p>
        {failure && <Callout tone="danger" role="alert">{failure}</Callout>}
        {pending.length ? (
          <FieldGroup legend="Topics to carry over">
            <div className="tw-flex tw-flex-col tw-gap-2">
              {pending.map((t) => (
                <Checkbox
                  key={t.index}
                  checked={chosen.has(t.index)}
                  onChange={(e) => setChosen((prev) => {
                    const next = new Set(prev);
                    if (e.target.checked) next.add(t.index);
                    else next.delete(t.index);
                    return next;
                  })}
                  label={t.parts > 1 ? `${t.name} (part ${t.part} of ${t.parts})` : t.name}
                />
              ))}
            </div>
          </FieldGroup>
        ) : (
          <Callout tone="success">Every topic of this {noun} is ticked as covered.</Callout>
        )}
        <FormField label="Note (optional)" hint="Kept in the batch’s plan history.">
          <Textarea rows={2} value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Ran out of time — doubts session" />
        </FormField>
      </form>
    </AppModal>
  );
};

export default CarryOverDialog;
